import { action } from "mobx";
import { PasswordSimilarityRequestModel } from "@api-types/partner-registration/Index";
import { registrationValidationSingle } from "@utils/membership/registrationValidation";
import { MainRegistrationStore } from "../MainRegistrationStore";
import {
	validateBooleanResponse,
	validatePasswordSimilarityResponse,
} from "@utils/lookups/validators";
import { PartnerLookupService } from "@services/membership/partner-registration";
import { PlatformPartnerRegistrationApiService } from "@api-services/platform";
import { RegistrationFormData } from "@data-types/membership/registration";
import { RootStore } from "@state/stores/types";
import { analyticsTracker } from "@utils/analytics";
import { RegistrationTypeEnum } from "@data-types/analytics-tracker/RegistrationTypeEnum";

export default class PartnerRegistrationStore extends MainRegistrationStore {
	rootStore: RootStore;
	constructor(rootStore: RootStore) {
		super();
		this.rootStore = rootStore;
	}

	@action.bound
	async onInitialize() {
		if (this.rootStore.isIpRestricted) {
			return;
		}

		analyticsTracker.trackRegistrationStarted(
			RegistrationTypeEnum.PartnerRegistration
		);

		this.loader.suspend();

		try {
			this.titleData = await PartnerLookupService.getTitles();
			this.countryData = await PartnerLookupService.getCountries();
			this.languageData = await PartnerLookupService.getLanguages();
			this.countryRegionsData =
				await PartnerLookupService.getCountryRegions();
			this.gamingLimitsData =
				await PartnerLookupService.getGamingLimits();
		} catch (error) {
			console.error(error);

			this.isRegistrationSuccessful = false;
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	async register(formData: RegistrationFormData, shopId: string) {
		try {
			this.loader.suspend();

			const body = this.prepareRegistrationFormData(
				formData,
				null,
				"aircash"
			);

			await PlatformPartnerRegistrationApiService.registerPartner(
				body,
				shopId
			);

			this.isRegistrationSuccessful = true;
			analyticsTracker.trackRegistrationFormCompleted();
		} catch (error) {
			const errorObject = await error.rawResponse?.json();

			registrationValidationSingle(errorObject);
		} finally {
			this.loader.resume();
			document.querySelector("main")?.scrollTo(0, 0);
		}
	}

	@action.bound
	async checkUserNameAvailability(username: string) {
		try {
			const response =
				await PlatformPartnerRegistrationApiService.checkUserNameAvailability(
					{
						validationProperty: username,
					}
				);
			return validateBooleanResponse(
				response,
				"partnerRegistrationStore-checkUserNameAvailability"
			);
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	@action.bound
	async checkIsEmailAvailable(email: string) {
		try {
			const response =
				await PlatformPartnerRegistrationApiService.checkEmailAvailability(
					{
						validationProperty: email,
					}
				);
			return validateBooleanResponse(
				response,
				"partnerRegistrationStore-checkIsEmailAvailable"
			);
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	@action.bound
	async checkPasswordSimilarity(
		dataset: PasswordSimilarityRequestModel
	): Promise<boolean> {
		let response = false;

		const { password, ...rest } = dataset;
		const valuesToCompareTo = [];

		for (const [_, value] of Object.entries(rest)) {
			if (value == null || value === "") {
				continue;
			}

			valuesToCompareTo.push(value);
		}

		try {
			const result =
				await PlatformPartnerRegistrationApiService.checkPasswordSimilarity(
					{
						validationProperty: password,
						compareWithValues: valuesToCompareTo,
					}
				);

			response = validatePasswordSimilarityResponse(
				result,
				"partnerRegistrationStore-checkPasswordSimilarity"
			);
		} catch (error) {
			console.error(error);
		}

		return response;
	}

	@action.bound
	onDispose() {
		this.currentStep = 1;
		this.isRegistrationSuccessful = null;

		//lookups
		this.titleData = [];
		this.countryData = [];
		this.languageData = [];
		this.countryRegionsData = [];
		this.gamingLimitsData = null;
	}
}
